import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SearchInput } from "../common/SearchInput";

import type { RagChatType } from "../../utils/rag/ragConfig";

interface RagItem {
  key: RagChatType;
  label: string;
  subtitle: string;
  icon: React.ReactNode;
}

interface RagGalleryProps {
  ragItems: RagItem[];
  shouldShowSearch: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  sortMode: 'default' | 'alphabetical';
  setSortMode: (mode: 'default' | 'alphabetical') => void;
  mode: string;
  blueAccent: string;
  secondaryColor: string;
  textColor: string;
  ITEMS_PER_PAGE: number;
  filteredRagItems: RagItem[];
  hasResults: boolean;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageDirection: number;
  setPageDirection: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  hasMultiplePages: boolean;
  paginatedItems: RagItem[];
  isFirstPage: boolean;
  isLastPage: boolean;
  handleSelect: (ragType: RagChatType) => void;
  SortDropdown: React.ComponentType<{
    sortMode: 'default' | 'alphabetical';
    onSortChange: (mode: 'default' | 'alphabetical') => void;
    isDark: boolean;
    secondaryColor: string;
  }>;
  RagButton: React.ComponentType<{
    item: RagItem;
    index: number;
    textColor: string;
    secondaryColor: string;
    isDark: boolean;
    onSelect: (key: RagChatType) => void;
    wrapperClassName?: string;
  }>;
}

export function RagGallery({
  ragItems,
  shouldShowSearch,
  searchQuery,
  setSearchQuery,
  sortMode,
  setSortMode,
  mode,
  blueAccent,
  secondaryColor,
  textColor,
  ITEMS_PER_PAGE,
  filteredRagItems,
  hasResults,
  currentPage,
  setCurrentPage,
  pageDirection,
  setPageDirection,
  totalPages,
  hasMultiplePages,
  paginatedItems,
  isFirstPage,
  isLastPage,
  handleSelect,
  SortDropdown,
  RagButton
}: RagGalleryProps) {
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | undefined>(undefined);

  // Measure and lock the height when seeing a full page of tiles
  useEffect(() => {
    if (gridContainerRef.current && paginatedItems.length === ITEMS_PER_PAGE && !lockedHeight) {
      requestAnimationFrame(() => {
        if (gridContainerRef.current) {
          setLockedHeight(gridContainerRef.current.offsetHeight);
        }
      });
    }
  }, [paginatedItems.length, ITEMS_PER_PAGE, lockedHeight]);

  // Reset lockedHeight when the window is resized (browser zoom triggers resize).
  // This forces a remeasure at the new zoom level so the pagination height stays accurate.
  useEffect(() => {
    const handleResize = () => setLockedHeight(undefined);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <motion.div
      key="rag-selector"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`flex flex-col w-full mt-5 flex-1 min-h-0`}
    >
      {/* Fixed header — title + search/sort, never scrolls */}
      <div className="max-w-5xl mx-auto w-full px-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 px-4">
          <p
            className="font-medium whitespace-nowrap"
            style={{
              color: blueAccent,
              fontFamily: "3MCircularTT",
              fontSize: "clamp(0.85rem, 1.2vw, 1.05rem)",
            }}
          >
            Available 3M AI Assistants for you:
          </p>
          <div className="flex items-center gap-3 sm:ml-auto">
            {shouldShowSearch && (
              <SearchInput
                id="rag-search-input"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search assistants..."
                containerClassName="w-full sm:w-64"
                inputClassName="placeholder-style top-heavy-input border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text)]"
                inputStyle={{ paddingTop: '0.625rem', paddingBottom: '0.625rem' }}
                iconColor={secondaryColor}
              />
            )}
            {shouldShowSearch && <SortDropdown
              sortMode={sortMode}
              onSortChange={setSortMode}
              isDark={mode === 'dark'}
              secondaryColor={secondaryColor}
            />}
          </div>
        </div>
      </div>

      {/* Scrollable gallery — only the tiles + pagination scroll */}
      {/* pb-10 ensures content clears the fixed DisclaimerNote bar at the bottom */}
      <div className="flex-1 min-h-0 overflow-y-auto chat-page-scroll pb-10">
        <div
          className="max-w-5xl mx-auto w-full px-2"
          style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gridTemplateRows: '1fr auto' }}
        >
          {hasMultiplePages && (
            <button
              onClick={() => { setPageDirection(-1); setCurrentPage((p) => Math.max(0, p - 1)); }}
              disabled={isFirstPage}
              className="p-2 rounded-full transition-colors self-center"
              style={{
                gridColumn: '1 / 2',
                gridRow: '1 / 2',
                color: isFirstPage ? (mode === 'dark' ? '#4b5563' : '#d1d5db') : secondaryColor,
                cursor: isFirstPage ? 'default' : 'pointer',
              }}
              aria-label="Previous page"
            >
              <ChevronLeft size={36} />
            </button>
          )}
          <div ref={gridContainerRef} className="relative px-4" style={{ gridColumn: '2 / 3', gridRow: '1 / 2', clipPath: "inset(-20% -10px -20% -10px)", minHeight: lockedHeight }}>
            {hasResults ? (
              <AnimatePresence initial={false} mode="wait" custom={pageDirection}>
                <motion.div
                  key={currentPage}
                  custom={pageDirection}
                  variants={{
                    enter: (dir) => ({ x: dir > 0 ? 200 : -200, opacity: 0 }),
                    center: { x: 0, opacity: 1 },
                    exit: (dir) => ({ x: dir > 0 ? -200 : 200, opacity: 0 }),
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start py-3 px-1"
                >
                  {paginatedItems.map((item, i) => {
                    const isLastOdd = paginatedItems.length % 2 !== 0 && i === paginatedItems.length - 1;
                    return (
                      <RagButton
                        key={item.key}
                        item={item}
                        index={i}
                        textColor={textColor}
                        secondaryColor={secondaryColor}
                        isDark={mode === 'dark'}
                        onSelect={handleSelect}
                        wrapperClassName={isLastOdd ? "sm:col-span-2 sm:w-1/2 sm:mx-auto" : ""}
                      />
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="flex min-h-[240px] items-center justify-center py-3 px-1">
                <p
                  className="text-center text-sm"
                  style={{
                    color: secondaryColor,
                    fontFamily: "3MCircularTT",
                  }}
                >
                  No assistants match your search.
                </p>
              </div>
            )}
          </div>
          {hasMultiplePages && (
            <button
              onClick={() => { setPageDirection(1); setCurrentPage((p) => Math.min(totalPages - 1, p + 1)); }}
              disabled={isLastPage}
              className="p-2 rounded-full transition-colors self-center"
              style={{
                gridColumn: '3 / 4',
                gridRow: '1 / 2',
                color: isLastPage ? (mode === 'dark' ? '#4b5563' : '#d1d5db') : secondaryColor,
                cursor: isLastPage ? 'default' : 'pointer',
              }}
              aria-label="Next page"
            >
              <ChevronRight size={36} />
            </button>
          )}
          {hasMultiplePages && (
            <div
              className="flex items-center justify-center gap-2 mt-4"
              style={{ gridColumn: '1 / 4', gridRow: '2 / 3' }}
            >
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setPageDirection(i > currentPage ? 1 : -1); setCurrentPage(i); }}
                  aria-label={`Go to page ${i + 1}`}
                  style={{
                    width: i === currentPage ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '9999px',
                    background: i !== currentPage ? 'color-mix(in srgb, var(--color-text) 50%, transparent)' : 'var(--color-text)',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    transition: 'width 0.25s ease, background 0.25s ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
